/**
 * PatientFilesTab.tsx
 *
 * Full patient file browser backed by EFS docstore.
 * Replaces legacy Google Drive section.
 */
import { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Camera,
  Download,
  FolderPlus,
  Pencil,
  Search,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  listFiles,
  downloadFile,
  uploadFile,
  deleteFile,
  createFolder,
  captureCamera,
  renameFile,
  nameLengthError,
  MAX_NAME_BYTES,
  type PatientFile,
} from '@/services/patient-files.service';
import { getErrorCode, getErrorMessage } from '@/services/apiClient';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui';
import { FileGrid } from './files/FileGrid';
import { FileLightbox } from './files/FileLightbox';
import { GoogleDriveContainer } from './files/GoogleDriveContainer';
import { CameraCaptureModal } from './files/CameraCaptureModal';
import { UploadDropzone } from './files/UploadDropzone';
import { describeUploadFailures, uploadEach, UPLOAD_FAILED_FALLBACK } from './files/uploadFailures';

interface PatientFilesTabProps {
  patientId: number;
}

export function PatientFilesTab({ patientId }: PatientFilesTabProps) {
  const queryClient = useQueryClient();
  const [currentFolder, setCurrentFolder] = useState('');
  const [search, setSearch] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [lightboxFile, setLightboxFile] = useState<PatientFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderError, setNewFolderError] = useState<string | null>(null);
  const [renamingFile, setRenamingFile] = useState<string | null>(null);

  const { data: files, isLoading } = useQuery({
    queryKey: ['patient-files', patientId, currentFolder],
    queryFn: () => listFiles(patientId, currentFolder || undefined),
    staleTime: 30_000,
  });

  const filteredFiles = useMemo(() => {
    if (!files) return [];
    if (!search.trim()) return files;
    const q = search.toLowerCase();
    return files.filter((f) => f.name.toLowerCase().includes(q));
  }, [files, search]);

  const invalidateFiles = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['patient-files', patientId] });
  }, [queryClient, patientId]);

  const handleToggleSelect = useCallback((name: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  }, []);

  async function handleUpload(uploadedFiles: File[]) {
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const failures = await uploadEach(
        uploadedFiles,
        (file, i) =>
          uploadFile(patientId, file, currentFolder || undefined, (pct) => {
            setUploadProgress(Math.round(((i + pct / 100) / uploadedFiles.length) * 100));
          }),
        UPLOAD_FAILED_FALLBACK,
      );
      if (failures.length === 0) {
        setShowUpload(false);
      } else {
        setError(describeUploadFailures(failures, uploadedFiles.length));
      }
    } finally {
      // Refreshed on failure too: the files saved around a refused one must
      // show, or they get uploaded twice.
      invalidateFiles();
      setUploading(false);
    }
  }

  async function handleDelete() {
    if (selectedFiles.size === 0) return;
    const targets = [...selectedFiles]
      .map((name) => files?.find((f) => f.name === name))
      .filter((f): f is PatientFile => Boolean(f));
    if (targets.length === 0) return;

    const folderCount = targets.filter((f) => f.type === 'folder').length;
    const fileCount = targets.length - folderCount;
    const what = [
      fileCount > 0 ? `${fileCount} file(s)` : '',
      folderCount > 0 ? `${folderCount} folder(s)` : '',
    ].filter(Boolean).join(' and ');
    const warning =
      `Permanently delete ${what}?\n\n` +
      `This erases ${targets.length === 1 ? 'it' : 'them'} from this patient's records. ` +
      'Deleted files cannot be recovered.';
    if (!window.confirm(warning)) return;

    setError(null);
    try {
      for (const file of targets) {
        try {
          await deleteFile(patientId, file.name, currentFolder || undefined, file.source, file.type);
        } catch (err) {
          // The API refuses a folder with contents once, so the second warning
          // is raised only when something would actually be thrown away.
          if (getErrorCode(err) !== 'FOLDER_NOT_EMPTY') throw err;
          const confirmed = window.confirm(
            `"${file.name}" is not empty.\n\n` +
              'Permanently delete the folder and every file inside it? This cannot be recovered.',
          );
          if (!confirmed) continue;
          await deleteFile(patientId, file.name, currentFolder || undefined, file.source, file.type, true);
        }
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Delete failed.'));
    } finally {
      // Whatever did get deleted before a failure should leave the grid.
      setSelectedFiles(new Set());
      invalidateFiles();
    }
  }

  async function handleDownloadSelected() {
    for (const name of selectedFiles) {
      const file = files?.find((f) => f.name === name);
      if (file && file.type === 'file') {
        await downloadFile(patientId, name, currentFolder || undefined, file.source);
      }
    }
  }

  async function handleCreateFolder() {
    const name = newFolderName.trim();
    if (!name) {
      setNewFolderError('Enter a folder name.');
      return;
    }
    // Checked again on save: maxLength stops the common case at the keyboard,
    // but a pasted or multi-byte name can still arrive over the limit.
    const tooLong = nameLengthError(name, 'Folder name');
    if (tooLong) {
      setNewFolderError(tooLong);
      return;
    }
    setError(null);
    setNewFolderError(null);
    try {
      await createFolder(patientId, name, currentFolder || undefined);
      setNewFolderName('');
      setShowNewFolder(false);
      invalidateFiles();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create folder.'));
    }
  }

  async function handleCameraCapture(base64: string) {
    try {
      await captureCamera(patientId, base64);
      invalidateFiles();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save photo.'));
      throw err;
    }
  }

  function handleFileClick(file: PatientFile) {
    if (file.isImage || file.mimeType === 'application/pdf') {
      setLightboxFile(file);
    } else {
      void downloadFile(patientId, file.name, currentFolder || undefined, file.source);
    }
  }

  async function handleRename(file: PatientFile, newName: string) {
    setError(null);
    const tooLong = nameLengthError(newName, file.type === 'folder' ? 'Folder name' : 'File name');
    if (tooLong) {
      setError(tooLong);
      setRenamingFile(null);
      return;
    }
    try {
      await renameFile(patientId, file.name, newName, file.type, currentFolder || undefined, file.source);
      setRenamingFile(null);
      setSelectedFiles(new Set());
      invalidateFiles();
    } catch (err) {
      setError(getErrorMessage(err, 'Rename failed.'));
      setRenamingFile(null);
    }
  }

  // Derived rather than stored, so it tracks the input on every keystroke while
  // newFolderError stays free to hold what the server said.
  const folderLengthError = nameLengthError(newFolderName.trim(), 'Folder name');
  const shownFolderError = folderLengthError ?? newFolderError;

  const breadcrumbs = currentFolder ? currentFolder.split('/') : [];

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Files</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => setShowUpload(!showUpload)}>
            <Upload className="h-3.5 w-3.5 mr-1" />
            Upload
          </Button>
          <Button size="sm" variant="secondary" onClick={() => { setShowNewFolder(!showNewFolder); setNewFolderError(null); }}>
            <FolderPlus className="h-3.5 w-3.5 mr-1" />
            New Folder
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setCameraOpen(true)}>
            <Camera className="h-3.5 w-3.5 mr-1" />
            Camera
          </Button>
          {selectedFiles.size === 1 && (
            <Button size="sm" variant="secondary" onClick={() => setRenamingFile([...selectedFiles][0])}>
              <Pencil className="h-3.5 w-3.5 mr-1" />
              Rename
            </Button>
          )}
          {selectedFiles.size > 0 && (
            <>
              <Button size="sm" variant="secondary" onClick={() => void handleDownloadSelected()}>
                <Download className="h-3.5 w-3.5 mr-1" />
                Download ({selectedFiles.size})
              </Button>
              <Button
                size="sm"
                variant="danger"
                title="Deletes permanently — removed files and folders cannot be recovered"
                onClick={() => void handleDelete()}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Delete ({selectedFiles.size})
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {showNewFolder && (
        <div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newFolderName}
              maxLength={MAX_NAME_BYTES}
              onChange={(e) => { setNewFolderName(e.target.value); if (newFolderError) setNewFolderError(null); }}
              placeholder="Folder name..."
              onKeyDown={(e) => e.key === 'Enter' && void handleCreateFolder()}
              aria-invalid={shownFolderError ? true : undefined}
              className={cn(
                'rounded-md border px-3 py-1.5 text-sm w-48 focus:outline-none focus:ring-1',
                shownFolderError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-slate-300 focus:border-blue-500 focus:ring-blue-500',
              )}
            />
            <Button
              size="sm"
              disabled={!newFolderName.trim() || Boolean(folderLengthError)}
              onClick={() => void handleCreateFolder()}
            >
              Create
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowNewFolder(false); setNewFolderName(''); setNewFolderError(null); }}>Cancel</Button>
          </div>
          {shownFolderError && <p className="mt-1 text-xs text-red-600">{shownFolderError}</p>}
        </div>
      )}

      {showUpload && (
        <UploadDropzone
          onFilesSelected={(f) => void handleUpload(f)}
          uploading={uploading}
          progress={uploadProgress}
        />
      )}

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <button type="button" onClick={() => { setCurrentFolder(''); setSelectedFiles(new Set()); }} className="hover:text-blue-600">
            Root
          </button>
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              <span>/</span>
              <button
                type="button"
                onClick={() => {
                  setCurrentFolder(breadcrumbs.slice(0, i + 1).join('/'));
                  setSelectedFiles(new Set());
                }}
                className="hover:text-blue-600"
              >
                {crumb}
              </button>
            </span>
          ))}
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="rounded-md border border-slate-300 pl-8 pr-3 py-1.5 text-sm w-48 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : (
        <FileGrid
          patientId={patientId}
          files={filteredFiles}
          currentFolder={currentFolder}
          onNavigate={(f) => { setCurrentFolder(f); setSelectedFiles(new Set()); }}
          onFileClick={handleFileClick}
          selectedFiles={selectedFiles}
          onToggleSelect={handleToggleSelect}
          renamingFile={renamingFile}
          onRename={(file, newName) => void handleRename(file, newName)}
          onRenameCancel={() => setRenamingFile(null)}
        />
      )}

      <GoogleDriveContainer patientId={patientId} onSynced={invalidateFiles} />

      {lightboxFile && (
        <FileLightbox
          open
          onClose={() => setLightboxFile(null)}
          patientId={patientId}
          fileName={lightboxFile.name}
          folder={currentFolder || undefined}
          source={lightboxFile.source}
          isImage={lightboxFile.isImage}
          mimeType={lightboxFile.mimeType}
          onDownload={() => void downloadFile(patientId, lightboxFile.name, currentFolder || undefined, lightboxFile.source)}
        />
      )}

      <CameraCaptureModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onCapture={handleCameraCapture}
      />
    </div>
  );
}
